import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, SimpleGrid, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError } from '@repletoryreach/sdk-api';
import { useT, type Translate } from '../../../../i18n/I18nProvider';
import { ArrowLeftIcon, CheckIcon, DotIcon, LockIcon, MailIcon } from '../../components/icons';

/**
 * Identity answers a spent or forged link with `INVALID_TOKEN` and a rejected
 * password with `WEAK_PASSWORD`, both of which deserve written copy. Anything
 * else keeps the server's own sentence, which is the only thing that carries
 * the reason.
 */
function messageFor(err: unknown, t: Translate): string {
  if (isApiError(err)) {
    if (err.code === 'INVALID_TOKEN') return t('auth.reset.errors.invalidToken');
    const reason = (err.body as { error?: { reason?: string } } | null)?.error?.reason;
    if (reason === 'breached') return t('password.errors.breached');
    if (err.code === 'WEAK_PASSWORD') return t('auth.reset.errors.weakPassword');
  }
  return err instanceof Error ? err.message : t('auth.reset.errors.failed');
}

/**
 * The rule identity enforces that a form can check: at least 12 characters
 * (since 2 October 2026; no capital, digit or symbol rule). Identity also
 * refuses the most common leaked passwords, which only it can check; that
 * refusal is translated in `messageFor`.
 */
const RULES: Array<{ key: string; test: (v: string) => boolean }> = [
  { key: 'auth.reset.rules.length', test: (v) => v.length >= 12 }
];

const STRENGTH_KEYS = ['auth.reset.strength.weak', 'auth.reset.strength.fair', 'auth.reset.strength.good', 'auth.reset.strength.strong'];

export function ResetForm({ token, email: initialEmail }: { token: string; email: string }) {
  const t = useT();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });

  // An address that arrived on the link belongs to the token: editing it can
  // only produce the generic invalid-token error, so the field is read-only
  // and the escape is a fresh reset rather than a typed correction.
  const fromLink = initialEmail !== '';

  const passed = RULES.map((rule) => rule.test(password));
  const passedCount = passed.filter(Boolean).length;
  const meetsPolicy = passedCount === RULES.length;
  // Length is what makes a password strong: 12 is fair, 16 good, 20 strong.
  const strengthLevel = password.length === 0 ? 0 : password.length < 12 ? 1 : password.length < 16 ? 2 : password.length < 20 ? 3 : 4;

  // Live per-field messages, so the two rules are not a surprise on submit.
  // They only speak once the field has been left, which keeps them quiet while
  // the operator is still typing.
  const passwordError = touched.password && password.length > 0 && password.length < 12
    ? t('auth.reset.errors.tooShort')
    : undefined;
  const confirmError = touched.confirm && confirm.length > 0 && password !== confirm
    ? t('auth.reset.errors.mismatch')
    : undefined;
  const matches = confirm.length > 0 && password === confirm;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) { setError(t('auth.reset.errors.tooShort')); return; }
    if (!meetsPolicy) { setError(t('auth.reset.errors.policy')); return; }
    if (password !== confirm) { setError(t('auth.reset.errors.mismatch')); return; }
    if (!token) { setError(t('auth.reset.errors.invalidLink')); return; }
    setBusy(true);
    try {
      await resetPassword(email, token, password);
      notifySuccess(t('auth.reset.toast.success'), t('auth.reset.toast.successTitle'));
      navigate('/auth/login', { replace: true });
    } catch (err) {
      setError(messageFor(err, t));
    } finally {
      setBusy(false);
    }
  }

  const linkIsBroken = error === t('auth.reset.errors.invalidLink') || error === t('auth.reset.errors.invalidToken');

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Stack gap="xs">
          <TextInput
            label={t('common.email')}
            leftSection={<MailIcon />}
            rightSection={fromLink ? <span className="admin-auth__chip">{t('auth.reset.fromLink')}</span> : undefined}
            rightSectionWidth={fromLink ? 86 : undefined}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            readOnly={fromLink}
            required
            autoComplete="email"
          />
          {fromLink && (
            <Link to="/auth/forgot-password" style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>
              {t('auth.reset.notYou')}
            </Link>
          )}
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <PasswordInput
            label={t('auth.reset.form.password')}
            leftSection={<LockIcon />}
            error={passwordError}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            onBlur={() => setTouched((s) => ({ ...s, password: true }))}
            required
            autoComplete="new-password"
            autoFocus
          />
          <Stack gap="xs">
            <PasswordInput
              label={t('auth.reset.form.confirm')}
              leftSection={<LockIcon />}
              error={confirmError}
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              onBlur={() => setTouched((s) => ({ ...s, confirm: true }))}
              required
              autoComplete="new-password"
            />
            {matches && (
              <Group gap="xs" align="center">
                <span style={{ color: 'var(--rr-color-success)', display: 'inline-flex' }}><CheckIcon size={15} /></span>
                <Text fz="xs" c="var(--rr-color-success)">{t('auth.reset.match')}</Text>
              </Group>
            )}
          </Stack>
        </SimpleGrid>

        {/* The meter counts the rules below rather than guessing at entropy, so
            the bar and the checklist can never disagree with each other. */}
        <Stack gap="xs">
          <Group gap="sm" align="center" wrap="nowrap">
            <span className="admin-auth__meter" aria-hidden>
              {STRENGTH_KEYS.map((key, i) => (
                <span key={key} className={`admin-auth__meter-seg${i < strengthLevel ? ' admin-auth__meter-seg--on' : ''}`} />
              ))}
            </span>
            {strengthLevel > 0 && (
              <Text fz="xs" c="var(--rr-color-text-muted)">{t(STRENGTH_KEYS[strengthLevel - 1])}</Text>
            )}
          </Group>

          <Text fz="xs" c="var(--rr-color-text-muted)">{t('auth.reset.rules.title')}</Text>
          <ul className="admin-auth__rules">
            {RULES.map((rule, i) => (
              <li key={rule.key} className={passed[i] ? 'admin-auth__rule-met' : undefined}>
                {passed[i] ? <CheckIcon size={15} /> : <DotIcon size={15} />}
                <Text fz="xs" c={passed[i] ? 'var(--rr-color-success)' : 'var(--rr-color-text-muted)'}>{t(rule.key)}</Text>
              </li>
            ))}
          </ul>
        </Stack>

        <div role="alert" aria-live="assertive">
          {error && (
            <Alert color="error">
              <Stack gap="xs" align="flex-start">
                <Text fz="sm">{error}</Text>
                {/* A dead end otherwise: the one thing a spent link needs is a
                    way to ask for a fresh one. */}
                {linkIsBroken && (
                  <Link to="/auth/forgot-password" style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>
                    {t('auth.reset.errors.invalidLinkAction')}
                  </Link>
                )}
              </Stack>
            </Alert>
          )}
        </div>

        <Button type="submit" loading={busy} fullWidth size="md">{t('auth.reset.submit')}</Button>

        {/* The endpoint revokes every refresh token on success, which is worth
            saying before the operator finds out on their other devices. */}
        <Group gap="xs" justify="center" align="center">
          <span style={{ color: 'var(--rr-color-text-muted)', display: 'inline-flex' }}><LockIcon size={14} /></span>
          <Text fz="xs" c="var(--rr-color-text-muted)">{t('auth.reset.otherSessions')}</Text>
        </Group>

        <Link to="/auth/login" className="admin-auth__row-link">
          <ArrowLeftIcon />
          {t('auth.backToSignIn')}
        </Link>
      </Stack>
    </form>
  );
}
